import type { DeploymentComponentProps, DeploymentData } from "./types";
import { computed, defineComponent } from "vue";
import { DEPLOYMENT_CONFIGS } from "@/pages/agents/deploy/deployment-config";
import { formatDidNumber } from "@/pages/agents/deploy/sip/did-management.utils";
import { useOwnedDidsQuery } from "@/queries/organization-did.query";
import { $t } from "@/services/i18n";
import DeploymentBadge from "./DeploymentBadge";

export default defineComponent<DeploymentComponentProps>({
  name: "AgentDeploymentsCell",
  props: {
    agent: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const dids = useOwnedDidsQuery();

    const linkedDid = computed(() =>
      dids.data.value?.find((d) => d.used_by_agent_id === props.agent.id)
    );

    const deployments = computed<DeploymentData[]>(() => {
      const deployments: DeploymentData[] = [];

      // WhatsApp Business deployment
      if (props.agent.deployments.whatsapp) {
        deployments.push({
          type: "whatsapp" as const,
          icon: DEPLOYMENT_CONFIGS.whatsapp.icon,
          color: DEPLOYMENT_CONFIGS.whatsapp.themeColor,
          value: props.agent.deployments.whatsapp.display_phone_number || "",
          disabled: props.agent.deployments.whatsapp.disabled || false,
          data: props.agent.deployments.whatsapp,
        });
      }

      // WhatsApp Personal (Baileys) deployment - only show if connected (has phone number)
      if (props.agent.deployments.whatsappBaileys?.phone_number) {
        deployments.push({
          type: "whatsapp_baileys" as const,
          icon: DEPLOYMENT_CONFIGS.whatsapp_baileys.icon,
          color: DEPLOYMENT_CONFIGS.whatsapp_baileys.themeColor,
          value: props.agent.deployments.whatsappBaileys.phone_number,
          disabled: props.agent.deployments.whatsappBaileys.disabled || false,
          data: props.agent.deployments.whatsappBaileys,
        });
      }

      // Chat Widget deployment
      if (props.agent.deployments.chatWidget) {
        deployments.push({
          type: "chat_widget" as const,
          icon: DEPLOYMENT_CONFIGS.chat_widget.icon,
          color: DEPLOYMENT_CONFIGS.chat_widget.themeColor,
          value: props.agent.deployments.chatWidget.key,
          disabled: props.agent.deployments.chatWidget.disabled || false,
          data: props.agent.deployments.chatWidget,
        });
      }

      // SIP deployment
      if (props.agent.deployments.sip) {
        const sipUri = `${props.agent.id}@${import.meta.env.VITE_SIP_DOMAIN || "sip.sippulse.ai"}`;
        deployments.push({
          type: "sip" as const,
          icon: DEPLOYMENT_CONFIGS.sip.icon,
          color: DEPLOYMENT_CONFIGS.sip.themeColor,
          value: linkedDid.value ? formatDidNumber(linkedDid.value.did) : sipUri,
          disabled:
            !props.agent.deployments.sip.enable_inbound &&
            !props.agent.deployments.sip.enable_outbound,
          data: props.agent.deployments.sip,
        });
      }

      // Telegram deployment
      if (props.agent.deployments.telegram) {
        deployments.push({
          type: "telegram" as const,
          icon: DEPLOYMENT_CONFIGS.telegram.icon,
          color: DEPLOYMENT_CONFIGS.telegram.themeColor,
          value: props.agent.deployments.telegram.botUsername
            ? `@${props.agent.deployments.telegram.botUsername}`
            : `Bot ${props.agent.deployments.telegram.botId}`,
          disabled: props.agent.deployments.telegram.disabled || false,
          data: props.agent.deployments.telegram,
        });
      }

      return deployments;
    });

    return () => {
      if (!deployments.value.length) {
        return <span class="text-xs text-muted-foreground opacity-50">{$t("none")}</span>;
      }

      return (
        <div class="flex flex-wrap items-center gap-2">
          {deployments.value.map((deployment) => (
            <DeploymentBadge key={deployment.type} agent={props.agent} deployment={deployment} />
          ))}
        </div>
      );
    };
  },
});
